import React from 'react';

// ═══════════════════════════════════════════════════════════════
// ConfirmModal — Reusable in-product confirmation dialog
//
// Replaces all window.confirm() calls with a warm, branded modal
// that matches the Talebound design system.
//
// Props:
//   title         string   — Modal heading (Lora serif)
//   message       string   — Body copy (Nunito/sans-serif)
//   confirmLabel  string   — Confirm button text (default: "Confirm")
//   cancelLabel   string   — Cancel button text (default: "Cancel")
//   onConfirm     fn       — Called when user confirms
//   onCancel      fn       — Called when user cancels or clicks overlay
//   destructive   bool     — If true, confirm button renders in red
// ═══════════════════════════════════════════════════════════════

export default function ConfirmModal({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  destructive = false,
}) {
  // Close on overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onCancel();
  };

  return (
    <div
      onClick={handleOverlayClick}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{
        background: '#fbf8f1',
        border: '1px solid rgba(15,23,42,0.12)',
        borderRadius: 16,
        width: '100%', maxWidth: 400,
        padding: '28px 28px 24px',
        boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
        position: 'relative',
      }}>
        {/* Decorative top line — matches feedback-modal pattern */}
        <div style={{
          position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
          width: '60%', height: 1,
          background: 'linear-gradient(90deg, transparent, rgba(15,23,42,0.15), transparent)',
        }} />

        {title && (
          <p style={{
            fontFamily: "'Lora', serif",
            fontSize: '1.05rem',
            fontWeight: 600,
            color: '#0f172a',
            margin: '0 0 10px',
          }}>
            {title}
          </p>
        )}

        {message && (
          <p style={{
            fontFamily: "'Nunito', 'Lato', sans-serif",
            fontSize: '0.95rem',
            color: '#3c4653',
            lineHeight: 1.6,
            margin: '0 0 24px',
          }}>
            {message}
          </p>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '9px 20px',
              fontFamily: "'Lora', serif",
              fontSize: '0.95rem',
              fontWeight: 500,
              background: 'rgba(15,23,42,0.05)',
              color: '#0f172a',
              border: '1px solid rgba(15,23,42,0.15)',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '9px 20px',
              fontFamily: "'Lora', serif",
              fontSize: '0.95rem',
              fontWeight: 600,
              // F-17: amber is the app-wide primary token; destructive stays red
              background: destructive ? '#c0392b' : 'linear-gradient(180deg, #AE6015 0%, #A85A0F 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
